import { useQueryClient } from '@tanstack/react-query'
import { usePage, useWebsiteSettings } from 'client/store'
import { pageSelectors } from 'client/store/page/pageSelectors'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { Tracking } from 'common/types/Page'
import React, { useEffect, useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { websiteTrackingAndSeoQuery } from 'src/editor/api/queries/website-tracking-and-seo-query'
import {
  SeoSettingsFields,
  updateWebsitePageAdvanced,
  updateWebsitePageRequest,
  updateWebsitePageSeo,
} from 'src/editor/api/website-pages-api'
import { useWebsiteUrl } from 'src/editor/shared/hooks/use-website-url'
import { CodeCircle03Icon } from 'src/editor/shared/icons/code-circle-03-icon'
import { InfoCircleIcon } from 'src/editor/shared/icons/info-circle-icon'
import { Server06Icon } from 'src/editor/shared/icons/server-06-icon'
import { Translate01Icon } from 'src/editor/shared/icons/translate-01-icon'
import { ModalWindowWithSidebar } from 'src/editor/shared/ui-lib/modal-window'
import { CommonModalWindowProps } from 'src/editor/shared/ui-lib/modal-window/types'
import { GeneralSettingsTab } from 'src/editor/shared/ui-lib/website-settings/tabs/general-settings-tab'
import { getWebsiteId } from 'src/editor/store/website-settings/website-settings-selector'
import { useAceAnnotations } from './hooks/use-ace-annotations'
import { AdvancedSettingsTab } from './tabs/advanced-settings-tab'
import { LocalesSettingsTab } from './tabs/locales-settings-tab'
import { SeoSettingsTab } from './tabs/seo-settings-tab'
export interface WebsiteSettingsProps
  extends Omit<CommonModalWindowProps, 'children'> {
  pageType: PageTypeEnum
  websitePageSetId: number
  websitePageId: number
  pageId: number
  initialTab?: string
}

export enum PageSettingsTabsEnum {
  General = 'general',
  Seo = 'seo',
  Languages = 'languages',
  Advanced = 'advanced',
}

export const WebsiteSettings = ({
  initialTab,
  pageType,
  websitePageSetId,
  websitePageId,
  pageId,
  onClose,
  ...commonModalWindowProps
}: WebsiteSettingsProps) => {
  const { t } = useTypedTranslation()
  const queryClient = useQueryClient()

  const [selectedItem, setSelectedItem] = useState<string>(
    initialTab ?? PageSettingsTabsEnum.General,
  )
  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const {
    annotations,
    handleValidate,
    isValid,
    validatehtml,
    hasHtmlError,
    isLoading: isHtmlValidateLoading,
  } = useAceAnnotations()

  const { data } = websitePagesQuery.useQuery({ queryFnArgs: [websiteId] })
  const { data: trackingAndSeo } = websiteTrackingAndSeoQuery.useQuery({
    queryFnArgs: [pageId],
  })
  const pageData = data?.pages.find(websitePageSet =>
    websitePageSet.locales.find(
      websitePage => websitePage.id === websitePageId,
    ),
  )
  const [name, setName] = useState(
    pageData?.locales.find(websitePage => websitePage.id === websitePageId)
      ?.name || '',
  )
  const {
    pathWithoutLocale,
    setPathWithoutLocale,
    path,
    websiteDomainName,
    websiteDomainNameWithLocale,
  } = useWebsiteUrl({ websitePageId })

  const pageTracking = usePage(pageSelectors.getTracking) as Tracking
  const [advancedFields, setAdvancedFields] = useState<Tracking>({
    ...pageTracking,
  })
  const [shouldRevalidate, setShouldRevalidate] = useState(false)

  const isDisabled =
    selectedItem === PageSettingsTabsEnum.General
      ? !(websiteId && name)
      : selectedItem === PageSettingsTabsEnum.Advanced
        ? !isValid && !!advancedFields?.headerCode
        : false

  const pageSeo = usePage(pageSelectors.getSeo) as SeoSettingsFields

  const [seoSettingsFields, setSeoSettingsFields] = useState<SeoSettingsFields>(
    {
      index: false,
      ...pageSeo,
    },
  )

  const updateSeoSettings =
    (field: keyof typeof seoSettingsFields) =>
    (val: SeoSettingsFields[keyof SeoSettingsFields]) => {
      setSeoSettingsFields(prev => ({ ...prev, [field]: val }))
    }

  useEffect(() => {
    trackingAndSeo?.tracking && setAdvancedFields(trackingAndSeo?.tracking)
    trackingAndSeo?.seo && setSeoSettingsFields(trackingAndSeo?.seo)
  }, [trackingAndSeo])

  const updateAdvancedSettings =
    (field: keyof typeof advancedFields) => (val: Tracking[keyof Tracking]) => {
      setAdvancedFields(prev => ({ ...prev, [field]: val }))
    }

  const onBeforeClose = () => {
    if (shouldRevalidate) {
      queryClient.invalidateQueries({
        queryKey: websiteId
          ? websitePagesQuery.getQueryKey(websiteId)
          : undefined,
      })
    }
    onClose?.()
  }

  const generalMutation = useMutationWithValidation({
    requestFunction: updateWebsitePageRequest,
    successToastMessage: t(
      'website_settings.sidebar.page_settings.updated_successfully',
    ),
    handleSuccess: () => setSelectedItem(PageSettingsTabsEnum.Seo),
    setQueryDataArgsOnSuccess: websitePagesQuery.setQueryDataArgs(
      [websiteId],
      prevData => ({
        ...prevData,
        pages: prevData.pages.map(websitePageSet =>
          websitePageSet.id === websitePageSetId
            ? {
                ...websitePageSet,
                locales: websitePageSet.locales.map(websitePage =>
                  websitePage.id === websitePageId
                    ? { ...websitePage, name, path: pathWithoutLocale }
                    : websitePage,
                ),
              }
            : websitePageSet,
        ),
      }),
    ),
    retry: false,
  })

  const seoMutation = useMutationWithValidation({
    requestFunction: updateWebsitePageSeo,
    successToastMessage: t(
      'website_settings.sidebar.page_settings.updated_successfully',
    ),
    handleSuccess: onClose,
    invalidateKeyOnSuccess: websiteTrackingAndSeoQuery.getQueryKey(pageId),
  })

  const advancedMutation = useMutationWithValidation({
    requestFunction: updateWebsitePageAdvanced,
    successToastMessage: t(
      'website_settings.sidebar.page_settings.updated_successfully',
    ),
    handleSuccess: onBeforeClose,
    invalidateKeyOnSuccess: websiteTrackingAndSeoQuery.getQueryKey(pageId),
  })

  const handleConfirm = async () => {
    setShouldRevalidate(true)
    switch (selectedItem) {
      case PageSettingsTabsEnum.General:
        generalMutation.fetcher({
          websitePageId,
          name,
          url: pathWithoutLocale,
        })
        return
      case PageSettingsTabsEnum.Seo:
        seoMutation.fetcher({
          ...seoSettingsFields,
          socialImage: seoSettingsFields.socialImage,
          pageContentId: pageId,
        })
        return
      case PageSettingsTabsEnum.Advanced:
        await validatehtml(advancedFields?.headerCode || '')
        advancedMutation.fetcher({
          ...advancedFields,
          pageId,
        })
        return
      default:
        return
    }
  }

  const menuItems = [
    {
      id: PageSettingsTabsEnum.General,
      label: t('website_settings.sidebar.page_settings.general'),
      icon: () => <InfoCircleIcon />,
      children: () => (
        <GeneralSettingsTab
          name={name}
          setName={setName}
          url={pathWithoutLocale}
          setUrl={setPathWithoutLocale}
          websiteDomainName={websiteDomainNameWithLocale}
        />
      ),
    },
    {
      id: PageSettingsTabsEnum.Seo,
      label: t('website_settings.sidebar.page_settings.seo'),
      icon: () => <Server06Icon />,
      children: () => (
        <SeoSettingsTab
          urlPath={path}
          websiteDomainName={websiteDomainName}
          fields={seoSettingsFields}
          updateField={updateSeoSettings}
        />
      ),
    },
    {
      id: PageSettingsTabsEnum.Languages,
      label: t('website_settings.sidebar.page_settings.languages'),
      icon: () => <Translate01Icon />,
      children: () => (
        <LocalesSettingsTab
          pageType={pageType}
          websitePageSetId={websitePageSetId}
        />
      ),
    },
    {
      id: PageSettingsTabsEnum.Advanced,
      label: t('website_settings.sidebar.page_settings.advanced.title'),
      icon: () => <CodeCircle03Icon />,
      children: () => (
        <AdvancedSettingsTab
          advancedFields={advancedFields}
          updateAdvancedSettings={updateAdvancedSettings}
          annotations={annotations}
          handleValidate={handleValidate}
          hasHtmlError={hasHtmlError}
        />
      ),
    },
  ]

  const onSelect = (item: string) => {
    setSelectedItem(item)
  }

  return (
    <ModalWindowWithSidebar
      menuItems={menuItems}
      title={t('website_settings.sidebar.page_settings.title')}
      selectedItem={selectedItem}
      onSelect={onSelect}
      cancelButtonProps={
        selectedItem !== PageSettingsTabsEnum.Languages
          ? {
              onClick: onBeforeClose,
              children: t('global.cancel'),
            }
          : undefined
      }
      confirmButtonProps={
        selectedItem !== PageSettingsTabsEnum.Languages
          ? {
              onClick: handleConfirm,
              isLoading:
                generalMutation.isLoading ||
                seoMutation.isLoading ||
                advancedMutation.isLoading ||
                isHtmlValidateLoading,
              disabled: isDisabled,
              children: t('core.header.save'),
            }
          : undefined
      }
      onClose={onBeforeClose}
      {...commonModalWindowProps}
    />
  )
}
